import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Home, Calendar, Radio, Tag, Inbox, Users,
  Code2, Settings as SettingsIcon, RefreshCw, Globe, Check,
  AlertCircle, ArrowUpRight, Mail, Send, Copy, Plus, X, Bed, Users2, MapPin,
  MessageCircle, Phone, Sparkles, Mic, Mail as MailIcon, BarChart3, CreditCard,
  Rocket, Activity, Eye, Megaphone, Clapperboard, ShieldCheck, Download, PlayCircle, PartyPopper, UtensilsCrossed
} from "lucide-react";
import { SiteGuard } from "./SiteGuard";
import { UserAccess } from "./UserAccess";
import { SystemAccess } from "./SystemAccess";
import { Gerd40Panel } from "./Gerd40Panel";
import { ComPanel, useComOpenCount } from "./ComPanel";
import { SitePanel, useSiteOpenCount } from "./SitePanel";
import { ImportCenter } from "./ImportCenter";
import { AuthGate } from "./Auth";
import { MarketingHub } from "./MarketingHub";
import { SocialStudio } from "./SocialStudio";
import { Newsletter } from "./Newsletter";
import { dict, languages, CHANNELS } from "./i18n";
import {
  ChatAgent, VoiceAgent, BookingAssistant, AdminAgent, MailHub, Analytics, Payments
} from "./AgentComponents";
import { ProjectManager, CostDashboard } from "./ProjectCost";
import { RoleSwitcher, PricingView, OwnerPortal, GuestSupport } from "./BusinessViews";
import { CompetitorYield } from "./CompetitorYield";
import { SunworkerLogo } from "./SunworkerLogo";
import { BookingCheckCard } from "./BookingCheck";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ======================================================
   Shared bits
   ====================================================== */
const StatusDot = ({ color = "#10B981" }) => (
  <span
    className="pulse-dot inline-block w-2 h-2 rounded-full"
    style={{ background: color }}
  />
);

const ChannelPill = ({ ch }) => {
  const c = CHANNELS[ch] || { label: ch, bg: "#374151", fg: "#fff" };
  return (
    <span className="channel-pill" style={{ background: c.bg, color: c.fg }} data-testid={`channel-pill-${ch}`}>
      {c.label}
    </span>
  );
};

/* ======================================================
   WP Admin Bar
   ====================================================== */
function WPAdminBar({ lang, setLang, onSync, t, role, setRole, roles, user, onLogout }) {
  return (
    <div className="bg-wp-bar text-gray-300 text-xs h-9 px-4 flex items-center justify-between border-b border-black/40 sticky top-0 z-50" data-testid="wp-admin-bar">
      <div className="flex items-center gap-4">
        <span className="font-bold text-white">WP</span>
        <span className="opacity-70">|</span>
        <span className="flex items-center gap-1.5"><Home size={12} /> {t.wpSite}</span>
        <span className="opacity-70">|</span>
        <span className="text-amber-400 font-mono text-[11px]">Sunworker · easybooking Edition</span>
      </div>
      <div className="flex items-center gap-4">
        {user?.role === "admin" && <RoleSwitcher role={role} setRole={setRole} roles={roles} />}
        <div className="flex items-center gap-1.5 text-emerald-400">
          <StatusDot color="#10B981" />
          <span className="font-mono text-[11px]">ShellManager · online</span>
        </div>
        <span className="font-mono text-[11px]" data-testid="current-user">{user?.email}</span>
        <button onClick={onLogout} data-testid="logout-button" className="hover:text-rose-400 transition">Abmelden</button>
        {user?.role === "admin" && (
          <button
            onClick={onSync}
            data-testid="sync-now-button"
            className="flex items-center gap-1 hover:text-amber-400 transition"
          >
            <RefreshCw size={12} /> {t.syncNow}
          </button>
        )}
        <div className="flex items-center gap-1">
          <Globe size={12} />
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            data-testid="language-switcher-dropdown"
            className="bg-transparent border-none text-[11px] p-0 pr-4 cursor-pointer text-gray-200"
            style={{ padding: 0, width: "auto" }}
          >
            {languages.map((l) => (
              <option key={l.code} value={l.code} style={{ background: "#1D2327" }}>
                {l.flag} {l.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/* ======================================================
   Sidebar
   ====================================================== */
const navItems = [
  { key: "dashboard", icon: LayoutDashboard },
  { key: "properties", icon: Home },
  { key: "import", icon: Download },
  { key: "calendar", icon: Calendar },
  { key: "channels", icon: Radio },
  { key: "rates", icon: Tag },
  { key: "inbox", icon: Inbox },
  { key: "guests", icon: Users },
  { key: "chat_agent", icon: MessageCircle },
  { key: "voice_agent", icon: Phone },
  { key: "booking_agent", icon: Sparkles },
  { key: "admin_agent", icon: Mic },
  { key: "project_agent", icon: Rocket },
  { key: "cost", icon: Activity },
  { key: "competitor", icon: Eye },
  { key: "marketing", icon: Megaphone },
  { key: "studio", icon: Clapperboard },
  { key: "newsletter", icon: Send },
  { key: "siteguard", icon: ShieldCheck },
  { key: "gerd40", icon: PartyPopper },
  { key: "com", icon: Globe },
  { key: "strandhussiedlung", icon: Bed },
  { key: "restaurant", icon: UtensilsCrossed },
  { key: "mail", icon: MailIcon },
  { key: "analytics", icon: BarChart3 },
  { key: "payments", icon: CreditCard },
  { key: "owner_portal", icon: Home },
  { key: "pricing", icon: Tag },
  { key: "support", icon: MessageCircle },
  { key: "shortcodes", icon: Code2 },
  { key: "settings", icon: SettingsIcon },
];

function Sidebar({ view, setView, t, role, allowed }) {
  const filteredItems = navItems.filter((n) =>
    allowed === "*" || !allowed || allowed.includes(n.key)
  );
  const comOpen = useComOpenCount(filteredItems.some((n) => n.key === "com"));
  const strandOpen = useSiteOpenCount("strandhussiedlung", filteredItems.some((n) => n.key === "strandhussiedlung"));
  const restOpen = useSiteOpenCount("restaurant", filteredItems.some((n) => n.key === "restaurant"));
  const badge = { com: comOpen, strandhussiedlung: strandOpen, restaurant: restOpen };
  return (
    <aside className="w-[240px] eb-surface border-r border-white/5 h-[calc(100vh-36px)] sticky top-9 flex flex-col" data-testid="sidebar-nav">
      <div className="px-5 py-5 border-b border-white/5">
        <div className="flex items-center gap-2">
          <SunworkerLogo size={32} />
          <div>
            <div className="font-heading font-bold text-sm leading-tight">Sunworker</div>
            <div className="font-mono text-[10px] text-eb-muted">KI &amp; IT · {role}</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 py-3 overflow-y-auto">
        {filteredItems.map(({ key, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setView(key)}
            data-testid={`nav-${key}`}
            className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-all border-l-2 ${
              view === key
                ? "border-eb-primary text-eb-primary bg-white/[0.03]"
                : "border-transparent text-eb-muted hover:text-eb-fg hover:bg-white/[0.02]"
            }`}
            style={view === key ? { borderLeftColor: "var(--eb-primary)" } : {}}
          >
            <Icon size={16} />
            <span className="font-medium">{t.nav[key] || key}</span>
            {key === "com" && comOpen > 0 && (
              <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-black text-[11px] font-bold flex items-center justify-center"
                title={`${comOpen} .com-Anfrage(n) warten auf Antwort`} data-testid="nav-com-badge">{comOpen}</span>
            )}
            {(key === "strandhussiedlung" || key === "restaurant") && badge[key] > 0 && (
              <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-black text-[11px] font-bold flex items-center justify-center"
                title={`${badge[key]} Anfrage(n) warten auf Antwort`} data-testid={`nav-${key}-badge`}>{badge[key]}</span>
            )}
          </button>
        ))}
      </nav>
      {role === "admin" && (
        <a
          href="/pitch/"
          target="_blank"
          rel="noreferrer"
          data-testid="nav-pitch-video"
          className="flex items-center gap-3 px-5 py-3 text-sm border-t border-white/5 text-eb-muted hover:text-eb-primary hover:bg-white/[0.02] transition-colors"
        >
          <PlayCircle size={16} />
          <span className="font-medium">Erklärvideo</span>
        </a>
      )}
      <div className="p-4 border-t border-white/5 text-[10px] font-mono text-eb-muted">
        <div>KI & IT MANAGEMENT</div>
        <div>SHORTCODE · [sunworker]</div>
      </div>
    </aside>
  );
}

/* ======================================================
   Dashboard
   ====================================================== */
function KpiCard({ label, value, suffix, accent = "primary", testId }) {
  const color = {
    primary: "var(--eb-primary)",
    emerald: "var(--eb-emerald)",
    amber: "var(--eb-amber)",
    sky: "var(--eb-sky)",
    rose: "var(--eb-rose)",
  }[accent];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="card relative overflow-hidden"
      data-testid={testId}
    >
      <div className="overline mb-2">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className="font-heading text-3xl font-extrabold tracking-tight" style={{ color }}>
          {value}
        </span>
        {suffix && <span className="text-eb-muted text-sm font-mono">{suffix}</span>}
      </div>
      <div className="absolute top-0 right-0 w-24 h-24 opacity-10 rounded-full blur-2xl" style={{ background: color }} />
    </motion.div>
  );
}

function OccupancyAmpel() {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/sites/strandhussiedlung/easybooking`).then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d || !d.connected) return null;
  const pct = d.totals.occupancy_pct;
  const level = pct >= 70 ? "emerald" : pct >= 30 ? "amber" : "rose";
  const label = pct >= 70 ? "gut ausgelastet" : pct >= 30 ? "mittel ausgelastet" : "viele frei – Vermarktung prüfen";
  const dot = { emerald: "bg-emerald-400", amber: "bg-amber-400", rose: "bg-rose-400" }[level];
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card flex items-center justify-between gap-4" data-testid="occupancy-ampel">
      <div>
        <div className="overline mb-1">Strandhussiedlung Rügen · Belegung</div>
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${dot}`} data-testid="occupancy-ampel-dot" />
          <span className="font-heading text-lg font-bold" data-testid="occupancy-ampel-label">{label}</span>
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-2xl font-bold" data-testid="occupancy-ampel-pct">{pct}%</div>
        <div className="text-xs text-eb-muted">{d.totals.free} von {d.totals.units} frei</div>
      </div>
    </motion.div>
  );
}

function Dashboard({ kpis, bookings, t }) {
  const nextCheckins = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return bookings
      .filter((b) => b.check_in >= today)
      .sort((a, b) => a.check_in.localeCompare(b.check_in))
      .slice(0, 6);
  }, [bookings]);

  return (
    <div className="space-y-6" data-testid="dashboard-view">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label={t.kpi.activeProperties} value={kpis.active_properties || 0} testId="kpi-active-properties" />
        <KpiCard label={t.kpi.occupancy} value={`${kpis.occupancy_pct || 0}`} suffix="%" accent="emerald" testId="kpi-occupancy" />
        <KpiCard label={t.kpi.upcoming} value={kpis.upcoming_checkins || 0} accent="sky" testId="kpi-upcoming" />
        <KpiCard label={t.kpi.revenue} value={`€ ${(kpis.revenue_30d_eur || 0).toLocaleString("de-DE")}`} accent="amber" testId="kpi-revenue" />
        <KpiCard label={t.kpi.inquiries} value={kpis.open_inquiries || 0} accent="rose" testId="kpi-inquiries" />
        <KpiCard label={t.kpi.channelHealth} value={`${kpis.channel_health_pct || 0}`} suffix="%" accent="emerald" testId="kpi-channel-health" />
      </div>

      <OccupancyAmpel />
      <BookingCheckCard />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-base">Upcoming check-ins</h3>
            <span className="overline">next 30 days</span>
          </div>
          <div className="space-y-2">
            {nextCheckins.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-2 border-b border-white/5" data-testid={`upcoming-${b.id}`}>
                <div className="flex items-center gap-3">
                  <div className="font-mono text-xs text-eb-muted w-20">{b.check_in}</div>
                  <div className="font-medium text-sm">{b.guest_name}</div>
                  <ChannelPill ch={b.channel} />
                </div>
                <div className="font-mono text-xs text-eb-muted">€ {b.total_eur}</div>
              </div>
            ))}
            {nextCheckins.length === 0 && <div className="text-eb-muted text-sm">—</div>}
          </div>
        </div>

        <div className="card">
          <h3 className="font-heading font-bold text-base mb-4">Channel share</h3>
          <div className="space-y-3">
            {Object.entries(CHANNELS).slice(0, 5).map(([key, c]) => {
              const count = bookings.filter((b) => b.channel === key).length;
              const total = bookings.length || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1">
                    <ChannelPill ch={key} />
                    <span className="font-mono text-xs">{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded">
                    <div className="h-full rounded" style={{ width: `${pct}%`, background: c.bg }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ======================================================
   Properties
   ====================================================== */
function Properties({ properties, t }) {
  return (
    <div className="space-y-5" data-testid="properties-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl">{t.properties}</h2>
          <p className="text-eb-muted text-sm">easybooking · Shell Manager</p>
        </div>
        <button className="btn btn-primary" data-testid="add-property-button">
          <Plus size={14} /> {t.addProperty}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {properties.map((p) => (
          <motion.div
            key={p.id}
            whileHover={{ y: -3 }}
            className="card !p-0 overflow-hidden"
            data-testid={`property-card-${p.easybooking_id}`}
          >
            <div className="aspect-[16/10] overflow-hidden bg-black/30">
              {p.images?.[0] && (
                <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-heading font-bold text-base">{p.name}</h4>
                <span className="font-mono text-[10px] text-eb-primary">{p.easybooking_id}</span>
              </div>
              <div className="flex items-center gap-1 text-eb-muted text-xs mb-3">
                <MapPin size={11} /> {p.location}
              </div>
              <div className="flex items-center gap-3 text-xs text-eb-muted mb-3">
                <span className="flex items-center gap-1"><Bed size={11} /> {p.bedrooms} {t.bedrooms}</span>
                <span className="flex items-center gap-1"><Users2 size={11} /> {p.sleeps} {t.sleeps}</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading font-bold text-lg text-eb-primary">€ {p.base_price_eur}</div>
                  <div className="text-[10px] text-eb-muted">{t.perNight}</div>
                </div>
                <div className="flex flex-wrap gap-1 justify-end">
                  {p.channels_enabled?.slice(0, 3).map((c) => <ChannelPill key={c} ch={c} />)}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ======================================================
   Calendar - multi property / channel colored
   ====================================================== */
function CalendarView({ properties, bookings }) {
  const days = useMemo(() => {
    const arr = [];
    const start = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, []);

  const bookingFor = (propId, date) => {
    const iso = date.toISOString().slice(0, 10);
    return bookings.find(
      (b) => b.property_id === propId && b.check_in <= iso && b.check_out > iso
    );
  };

  return (
    <div className="space-y-4" data-testid="calendar-view">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl">Belegungskalender</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {Object.keys(CHANNELS).map((c) => <ChannelPill key={c} ch={c} />)}
        </div>
      </div>
      <div className="card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <div style={{ minWidth: 30 * 44 + 220 }}>
            <div className="grid" style={{ gridTemplateColumns: `220px repeat(30, 44px)` }}>
              <div className="eb-surface px-3 py-2 border-r border-b border-white/5 overline">Objekt</div>
              {days.map((d, i) => (
                <div key={i} className="eb-surface border-r border-b border-white/5 text-center py-2">
                  <div className="font-mono text-[10px] text-eb-muted">{d.toLocaleDateString("de", { weekday: "short" }).slice(0, 2)}</div>
                  <div className="font-mono text-xs">{d.getDate()}</div>
                </div>
              ))}
              {properties.map((p) => (
                <React.Fragment key={p.id}>
                  <div className="px-3 py-3 border-r border-b border-white/5 flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-eb-elev flex-shrink-0" style={{ backgroundImage: `url(${p.images?.[0]})`, backgroundSize: "cover" }} />
                    <div className="min-w-0">
                      <div className="font-medium text-xs truncate">{p.name}</div>
                      <div className="font-mono text-[10px] text-eb-muted">{p.easybooking_id}</div>
                    </div>
                  </div>
                  {days.map((d, i) => {
                    const b = bookingFor(p.id, d);
                    const c = b ? CHANNELS[b.channel] : null;
                    return (
                      <div key={i} className="cal-cell" data-testid={`cal-${p.id}-${i}`}>
                        {b && (
                          <div
                            className="absolute inset-1 rounded flex items-center justify-center text-[9px] font-mono font-bold overflow-hidden"
                            style={{ background: c?.bg, color: c?.fg }}
                            title={`${b.guest_name} · ${b.check_in} → ${b.check_out}`}
                          >
                            {b.guest_name.split(" ")[0].slice(0, 4)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ======================================================
   Channels
   ====================================================== */
function Channels({ t }) {
  const [status, setStatus] = useState([]);
  const [syncing, setSyncing] = useState(null);
  const load = async () => {
    const r = await axios.get(`${API}/channels/status`);
    setStatus(r.data);
  };
  useEffect(() => { load(); }, []);

  const sync = async (ch) => {
    setSyncing(ch);
    await axios.post(`${API}/channels/sync`, {}, { params: ch ? { channel: ch } : {} });
    await load();
    setTimeout(() => setSyncing(null), 600);
  };

  return (
    <div className="space-y-5" data-testid="channels-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl">{t.channelsTitle}</h2>
          <p className="text-eb-muted text-sm">{t.channelsSubtitle}</p>
        </div>
        <button className="btn btn-primary" onClick={() => sync(null)} data-testid="sync-all-button">
          <RefreshCw size={14} /> {t.syncAll}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {status.map((s) => {
          const c = CHANNELS[s.channel];
          return (
            <div key={s.channel} className="card" data-testid={`channel-status-${s.channel}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded flex items-center justify-center font-heading font-extrabold text-xs" style={{ background: c.bg, color: c.fg }}>
                    {c.label.slice(0, 2)}
                  </div>
                  <div>
                    <div className="font-heading font-bold text-sm">{c.label}</div>
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <StatusDot color={s.connected ? "#10B981" : "#F43F5E"} />
                      <span className={s.connected ? "text-emerald-400" : "text-rose-400"}>
                        {s.connected ? t.connected : t.disconnected}
                      </span>
                    </div>
                  </div>
                </div>
                <button className="btn !p-2" onClick={() => sync(s.channel)} data-testid={`sync-${s.channel}`}>
                  <RefreshCw size={12} className={syncing === s.channel ? "animate-spin" : ""} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
                <div>
                  <div className="text-eb-muted">{t.lastSync}</div>
                  <div>{new Date(s.last_sync).toLocaleTimeString("de", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <div>
                  <div className="text-eb-muted">{t.latency}</div>
                  <div>{s.latency_ms} ms</div>
                </div>
                <div>
                  <div className="text-eb-muted">Objekte</div>
                  <div>{s.properties_synced}</div>
                </div>
                <div>
                  <div className="text-eb-muted">{t.errors}</div>
                  <div className={s.errors > 0 ? "text-rose-400" : ""}>{s.errors}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ======================================================
   Rates (bulk editor)
   ====================================================== */
function Rates({ properties, t }) {
  const [sel, setSel] = useState(properties[0]?.id || "");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [price, setPrice] = useState("");
  const [channels, setChannels] = useState(["easybooking", "booking_com", "airbnb"]);
  const [msg, setMsg] = useState("");

  useEffect(() => { if (!sel && properties[0]) setSel(properties[0].id); }, [properties, sel]);

  const toggle = (c) => setChannels((prev) => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);

  const push = async () => {
    await axios.post(`${API}/rates`, {
      property_id: sel,
      start_date: start,
      end_date: end,
      price_eur: parseFloat(price || 0),
      push_to_channels: channels,
    });
    setMsg(`✓ Preis € ${price} auf ${channels.length} Kanäle gepusht`);
    setTimeout(() => setMsg(""), 3000);
  };

  return (
    <div className="space-y-5" data-testid="rates-view">
      <div>
        <h2 className="font-heading font-bold text-2xl">{t.bulkRateEdit}</h2>
        <p className="text-eb-muted text-sm">Zeitraum wählen · Preis setzen · direkt an alle Kanäle pushen</p>
      </div>
      <div className="card max-w-3xl">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="overline mb-1">Objekt</div>
            <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="rate-property-select">
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <div className="overline mb-1">Preis / Nacht (€)</div>
            <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="rate-edit-input-price" placeholder="480" />
          </div>
          <div>
            <div className="overline mb-1">Von</div>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="rate-start" />
          </div>
          <div>
            <div className="overline mb-1">Bis</div>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="rate-end" />
          </div>
        </div>
        <div className="mt-4">
          <div className="overline mb-2">{t.pushToChannels}</div>
          <div className="flex gap-2 flex-wrap">
            {Object.keys(CHANNELS).map((c) => (
              <button
                key={c}
                onClick={() => toggle(c)}
                data-testid={`rate-channel-${c}`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs transition"
                style={{
                  borderColor: channels.includes(c) ? CHANNELS[c].bg : "var(--eb-border)",
                  background: channels.includes(c) ? CHANNELS[c].bg : "transparent",
                  color: channels.includes(c) ? CHANNELS[c].fg : "var(--eb-fg)",
                }}
              >
                {channels.includes(c) && <Check size={12} />}
                {CHANNELS[c].label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <button className="btn btn-primary" onClick={push} data-testid="rate-push-button">
            <ArrowUpRight size={14} /> {t.pushToChannels}
          </button>
          {msg && <span className="text-emerald-400 text-sm">{msg}</span>}
        </div>
      </div>
    </div>
  );
}

/* ======================================================
   Inbox - AI auto reply + escalate
   ====================================================== */
function InquiryInbox({ t, lang }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/inquiries`);
    setItems(r.data);
    if (r.data.length) setSel((prev) => prev || r.data[0]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    await axios.post(`${API}/inquiries/${sel.id}/send-reply`);
    await load();
  };
  const escalate = async () => {
    await axios.post(`${API}/inquiries/${sel.id}/escalate`);
    await load();
  };

  const statusColor = (s) => ({
    new: "#F59E0B",
    auto_replied: "#38BDF8",
    escalated: "#F43F5E",
    resolved: "#10B981",
  }[s] || "#9CA3AF");

  return (
    <div className="space-y-4" data-testid="inbox-view">
      <div>
        <h2 className="font-heading font-bold text-2xl">{t.inboxTitle}</h2>
        <p className="text-eb-muted text-sm">{t.inboxSubtitle}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[380px_1fr] gap-4">
        <div className="card !p-0 overflow-hidden">
          {items.map((i) => (
            <button
              key={i.id}
              onClick={() => setSel(i)}
              data-testid={`inquiry-row-${i.id}`}
              className={`w-full text-left px-4 py-3 border-b border-white/5 transition hover:bg-white/[0.03] ${sel?.id === i.id ? "bg-white/[0.04]" : ""}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-sm">{i.guest_name}</span>
                <StatusDot color={statusColor(i.status)} />
              </div>
              <div className="flex items-center justify-between text-[11px] text-eb-muted mb-1">
                <span>{i.guest_email}</span>
                <span className="font-mono uppercase">{i.language}</span>
              </div>
              <div className="text-xs text-eb-muted line-clamp-2">{i.message}</div>
            </button>
          ))}
          {items.length === 0 && <div className="p-6 text-eb-muted text-sm text-center">—</div>}
        </div>

        {sel && (
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-heading font-bold text-lg">{sel.guest_name}</div>
                <div className="text-eb-muted text-xs flex items-center gap-2">
                  <Mail size={11} /> {sel.guest_email}
                  <span className="font-mono uppercase ml-2 px-1.5 py-0.5 rounded bg-white/5">{sel.language}</span>
                </div>
              </div>
              <span
                className="channel-pill"
                style={{ background: statusColor(sel.status) + "22", color: statusColor(sel.status), border: `1px solid ${statusColor(sel.status)}44` }}
                data-testid={`inquiry-status-${sel.id}`}
              >
                {t.status[sel.status] || sel.status}
              </span>
            </div>

            <div className="eb-surface rounded p-3 text-sm leading-relaxed border border-white/5 mb-4">
              {sel.message}
            </div>

            <div className="overline mb-2">🤖 {t.autoReplyDraft}</div>
            <textarea
              value={sel.auto_reply_draft}
              onChange={(e) => setSel({ ...sel, auto_reply_draft: e.target.value })}
              rows={9}
              data-testid="auto-reply-textarea"
              className="font-mono text-xs"
            />
            <div className="flex gap-2 mt-4">
              <button className="btn btn-primary" onClick={send} data-testid="inquiry-auto-reply-send-button">
                <Send size={13} /> {t.sendAutoReply}
              </button>
              <button className="btn btn-amber" onClick={escalate} data-testid="escalate-to-reception-button">
                <AlertCircle size={13} /> {t.escalate}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ======================================================
   Guests
   ====================================================== */
function Guests({ bookings }) {
  return (
    <div className="space-y-4" data-testid="guests-view">
      <h2 className="font-heading font-bold text-2xl">Gäste-CRM</h2>
      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="eb-surface">
              <th className="text-left px-4 py-3 overline">Gast</th>
              <th className="text-left px-4 py-3 overline">E-Mail</th>
              <th className="text-left px-4 py-3 overline">Kanal</th>
              <th className="text-left px-4 py-3 overline">Anreise</th>
              <th className="text-left px-4 py-3 overline">Nächte</th>
              <th className="text-right px-4 py-3 overline">Umsatz</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => {
              const nights = Math.max(1, (new Date(b.check_out) - new Date(b.check_in)) / 86400000);
              return (
                <tr key={b.id} className="border-t border-white/5" data-testid={`guest-row-${b.id}`}>
                  <td className="px-4 py-3 font-medium">{b.guest_name}</td>
                  <td className="px-4 py-3 text-eb-muted">{b.guest_email}</td>
                  <td className="px-4 py-3"><ChannelPill ch={b.channel} /></td>
                  <td className="px-4 py-3 font-mono text-xs">{b.check_in}</td>
                  <td className="px-4 py-3 font-mono text-xs">{nights}</td>
                  <td className="px-4 py-3 text-right font-mono">€ {b.total_eur}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ======================================================
   Shortcodes
   ====================================================== */
function Shortcodes({ properties, t }) {
  const [copied, setCopied] = useState("");
  const codes = [
    { label: "Alle Objekte (Grid)", code: '[easybooking_properties layout="grid" columns="3"]' },
    { label: "Einzelnes Objekt", code: `[easybooking_property id="${properties[0]?.easybooking_id || "EB-10234"}"]` },
    { label: "Buchungs-Kalender", code: `[easybooking_calendar property_id="${properties[0]?.easybooking_id || "EB-10234"}" months="3"]` },
    { label: "Verfügbarkeits-Suche", code: '[easybooking_search destination="Tirol"]' },
    { label: "Preistabelle", code: `[easybooking_rates property_id="${properties[0]?.easybooking_id || "EB-10234"}"]` },
    { label: "Anfrage-Formular", code: `[easybooking_inquiry_form property_id="${properties[0]?.easybooking_id || "EB-10234"}" lang="de"]` },
  ];
  const copy = async (code) => {
    await navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(""), 1500);
  };
  return (
    <div className="space-y-5" data-testid="shortcodes-view">
      <div>
        <h2 className="font-heading font-bold text-2xl">{t.shortcodeTitle}</h2>
        <p className="text-eb-muted text-sm">{t.shortcodeHint}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {codes.map((c) => (
          <div key={c.code} className="card">
            <div className="overline mb-2">{c.label}</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 eb-surface rounded px-3 py-2 font-mono text-xs text-amber-400 border border-white/5 overflow-x-auto">
                {c.code}
              </code>
              <button className="btn" onClick={() => copy(c.code)} data-testid={`divi-shortcode-copy-button-${c.code.length}`}>
                {copied === c.code ? <><Check size={12} /> {t.copied}</> : <><Copy size={12} /> {t.copy}</>}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="card border-dashed" style={{ borderColor: "rgba(212, 175, 55, 0.3)" }}>
        <div className="overline mb-2 text-eb-primary">Divi Cloud Modules</div>
        <div className="grid md:grid-cols-3 gap-3">
          {["EB Property Grid", "EB Booking Calendar", "EB Inquiry Form", "EB Rate Table", "EB Hero Search", "EB Channel Badge"].map((m) => (
            <div key={m} className="eb-surface rounded p-3 border border-white/5 flex items-center justify-between">
              <span className="text-xs font-semibold">{m}</span>
              <span className="channel-pill" style={{ background: "var(--eb-primary)", color: "#0B0F17" }}>DIVI</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ======================================================
   Settings
   ====================================================== */
function SettingsView({ t }) {
  const [s, setS] = useState({});
  const [saved, setSaved] = useState(false);
  useEffect(() => { axios.get(`${API}/settings`).then((r) => setS(r.data || {})); }, []);
  const save = async () => {
    await axios.put(`${API}/settings`, s);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  const field = (key, label, type = "text") => (
    <div>
      <div className="overline mb-1">{label}</div>
      <input
        type={type}
        value={s[key] ?? ""}
        onChange={(e) => setS({ ...s, [key]: type === "number" ? parseInt(e.target.value || 0) : e.target.value })}
        data-testid={`setting-${key}`}
      />
    </div>
  );
  return (
    <div className="space-y-5" data-testid="settings-view">
      <h2 className="font-heading font-bold text-2xl">{t.settings.title}</h2>
      <div className="card max-w-3xl">
        <div className="grid md:grid-cols-2 gap-4">
          {field("shell_manager_id", t.settings.shellManager)}
          {field("easybooking_api_key", t.settings.apiKey)}
          {field("easybooking_property_id", t.settings.propertyId)}
          {field("booking_com_hotel_id", t.settings.bookingHotel)}
          {field("airbnb_listing_id", t.settings.airbnbListing)}
          {field("reception_email", t.settings.receptionEmail, "email")}
          {field("escalation_minutes", t.settings.escalationMinutes, "number")}
          <div className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              checked={!!s.auto_reply_enabled}
              onChange={(e) => setS({ ...s, auto_reply_enabled: e.target.checked })}
              data-testid="setting-auto-reply"
              style={{ width: "auto" }}
            />
            <span className="text-sm">{t.settings.autoReply}</span>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <button className="btn btn-primary" onClick={save} data-testid="settings-save-button">
            {t.settings.save}
          </button>
          {saved && <span className="text-emerald-400 text-sm">✓ gespeichert</span>}
        </div>
      </div>

      <div className="card max-w-3xl">
        <div className="overline mb-3">LLM-Anbieter (DeutschlandGPT · Groq · Gemini Free · Ollama …)</div>
        <p className="text-xs text-eb-muted mb-3">
          Standard: Claude Sonnet 5.5 via Emergent Key. <strong className="text-eb-primary">DeutschlandGPT</strong> ist ein DSGVO-konformer EU-Aggregator mit 50+ Modellen (GPT, Claude, Gemini, Llama, Mistral).
          Für feingranulares Routing pro Agent → <code className="text-amber-400">KI-Kosten → Routing</code>.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="overline mb-1">Anbieter</div>
            <select
              value={s.llm_provider || "emergent"}
              onChange={(e) => {
                const v = e.target.value;
                const presets = {
                  deutschlandgpt: { llm_base_url: "https://apiv2.deutschlandgpt.de/platform-api/api/v2", llm_model: "gpt-5.4" },
                  groq:           { llm_base_url: "https://api.groq.com/openai/v1", llm_model: "llama-3.3-70b-versatile" },
                  gemini_free:    { llm_base_url: "https://generativelanguage.googleapis.com/v1beta/openai", llm_model: "gemini-2.0-flash-exp" },
                  ollama:         { llm_base_url: "http://localhost:11434/v1", llm_model: "llama3.3:70b" },
                  deepseek:       { llm_base_url: "https://api.deepseek.com/v1", llm_model: "deepseek-chat" },
                };
                setS({ ...s, llm_provider: v, ...(presets[v] || {}) });
              }}
              data-testid="setting-llm-provider"
            >
              <option value="emergent">Emergent (Claude Sonnet 5.5) — Standard</option>
              <option value="deutschlandgpt">🇩🇪 DeutschlandGPT (EU · DSGVO · 50+ Modelle)</option>
              <option value="groq">🟢 Groq (Llama 3.3 70B) — KOSTENLOS</option>
              <option value="gemini_free">🟢 Gemini 2.0 Flash — KOSTENLOS</option>
              <option value="ollama">🟢 Ollama (lokal, self-hosted)</option>
              <option value="deepseek">DeepSeek V3 — sehr günstig</option>
              <option value="openai_compatible">Eigener OpenAI-kompatibler Server</option>
            </select>
          </div>
          {field("llm_base_url", "Base URL")}
          {field("llm_api_key", "API-Key", "password")}
          {field("llm_model", "Modell")}
        </div>
      </div>

      <div className="card max-w-3xl">
        <div className="overline mb-3">Telefonie / Telekom SIP-Trunk</div>
        <p className="text-xs text-eb-muted mb-3">Für Produktion: Asterisk + Telekom SIP-Trunk. Config-Vorlage in <code className="text-amber-400">sip-telephony/pjsip.telekom.sample.conf</code>.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="overline mb-1">Anbieter</div>
            <select
              value={s.sip_provider || ""}
              onChange={(e) => setS({ ...s, sip_provider: e.target.value })}
              data-testid="setting-sip-provider"
            >
              <option value="">—</option>
              <option value="telekom">Telekom DeutschlandLAN / CompanyFlex</option>
              <option value="sipgate">sipgate</option>
              <option value="custom">Eigener SIP-Provider</option>
            </select>
          </div>
          {field("sip_domain", "SIP-Domain (z.B. tel.t-online.de)")}
          {field("sip_user", "SIP-Benutzer (+49…)")}
          {field("sip_password", "SIP-Passwort", "password")}
          {field("sip_trunk_name", "Trunk-Name")}
        </div>
      </div>

      <UserAccess />

      <SystemAccess />

      <div className="card max-w-3xl">
        <div className="overline mb-3">Plugin Download</div>
        <p className="text-sm text-eb-muted mb-3">
          Das vollständige WordPress-Plugin (PHP) wird im Projektordner unter{" "}
          <code className="font-mono text-amber-400">/app/wordpress-plugin/easybooking-wp</code> bereitgestellt.
          Zip erstellen und über <strong>Plugins → Hochladen</strong> in WordPress installieren.
        </p>
        <code className="eb-surface rounded px-3 py-2 font-mono text-xs text-amber-400 border border-white/5 inline-block">
          cd /app/wordpress-plugin && zip -r easybooking-wp.zip easybooking-wp
        </code>
      </div>
    </div>
  );
}

/* ======================================================
   App root
   ====================================================== */
export default function App() {
  return <AuthGate>{(user, onLogout) => <Cockpit user={user} onLogout={onLogout} />}</AuthGate>;
}

function Cockpit({ user, onLogout }) {
  const [lang, setLang] = useState("de");
  const [view, setView] = useState(() => (window.location.pathname === "/auth/google" ? (sessionStorage.getItem("sn-gsc-site") || "com") : new URLSearchParams(window.location.search).get("view") || "dashboard"));
  const [role, setRole] = useState(user.role || "manager");
  const [roles, setRoles] = useState({});
  const [allowed, setAllowed] = useState("*");
  const [kpis, setKpis] = useState({});
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [toast, setToast] = useState("");
  const t = dict[lang];

  useEffect(() => {
    axios.get(`${API}/dashboard/kpis`).then((r) => setKpis(r.data)).catch(() => {});
    axios.get(`${API}/properties`).then((r) => setProperties(r.data)).catch(() => {});
    axios.get(`${API}/bookings`).then((r) => setBookings(r.data)).catch(() => {});
    axios.get(`${API}/billing/plans`).then((r) => setRoles(r.data.roles || {})).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/billing/role/${role}/nav`).then((r) => {
      const a = r.data.allowed;
      setAllowed(a);
      // if current view isn't allowed, bounce to the first allowed view
      if (r_allowed_check(view, a) === false) setView(Array.isArray(a) && a.length ? a[0] : "dashboard");
    });
  }, [role]); // eslint-disable-line

  function r_allowed_check(v, a) {
    if (!a || a === "*") return true;
    return a.includes(v);
  }

  const syncAll = async () => {
    try {
      await axios.post(`${API}/channels/sync`);
      setToast("✓ Alle Kanäle synchronisiert");
    } catch {
      setToast("Synchronisierung fehlgeschlagen");
    }
    setTimeout(() => setToast(""), 2500);
  };

  const views = {
    dashboard: <Dashboard kpis={kpis} bookings={bookings} t={t} />,
    properties: <Properties properties={properties} t={t} />,
    import: <ImportCenter />,
    calendar: <CalendarView properties={properties} bookings={bookings} />,
    channels: <Channels t={t} />,
    rates: <Rates properties={properties} t={t} />,
    inbox: <InquiryInbox t={t} lang={lang} />,
    guests: <Guests bookings={bookings} />,
    chat_agent: <ChatAgent lang={lang} />,
    voice_agent: <VoiceAgent lang={lang} />,
    booking_agent: <BookingAssistant lang={lang} properties={properties} />,
    admin_agent: <AdminAgent lang={lang} />,
    project_agent: <ProjectManager lang={lang} />,
    cost: <CostDashboard />,
    competitor: <CompetitorYield />,
    marketing: <MarketingHub />,
    studio: <SocialStudio />,
    newsletter: <Newsletter />,
    siteguard: <SiteGuard />,
    gerd40: <Gerd40Panel />,
    com: <ComPanel />,
    strandhussiedlung: <SitePanel slug="strandhussiedlung" />,
    restaurant: <SitePanel slug="restaurant" />,
    mail: <MailHub />,
    analytics: <Analytics />,
    payments: <Payments properties={properties} />,
    owner_portal: <OwnerPortal />,
    pricing: <PricingView />,
    support: <GuestSupport lang={lang} />,
    shortcodes: <Shortcodes properties={properties} t={t} />,
    settings: <SettingsView t={t} />,
  };

  return (
    <div className="min-h-screen eb-bg grain" data-testid="app-root">
      <WPAdminBar lang={lang} setLang={setLang} onSync={syncAll} t={t} role={role} setRole={setRole} roles={roles} user={user} onLogout={onLogout} />
      <div className="flex">
        <Sidebar view={view} setView={setView} t={t} role={role} allowed={allowed} />
        <main className="flex-1 min-w-0 p-5 md:p-8 relative z-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
            >
              {views[view]}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 bg-emerald-500 text-black px-4 py-2 rounded-md font-semibold text-sm z-50"
            data-testid="toast"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
